import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import classNames from 'classnames';
import { useTranslation } from '../../config/i18n';
import styles from './Input.module.css';

// Explicit style mappings for CSS modules (dynamic keys don't work with tree-shaking)
type InputSize = 'sm' | 'md' | 'lg' | 'extraLg';
const sizeStyles: Record<InputSize, string> = {
  sm: styles['container--sm'],
  md: styles['container--md'],
  lg: styles['container--lg'],
  extraLg: styles['container--extraLg'],
};

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * Label text displayed above the input
   */
  label?: string;

  /**
   * Error message displayed below the input
   */
  error?: string;

  /**
   * Helper text displayed below the input (when no error)
   */
  helperText?: string;

  /**
   * Whether the input is required
   */
  required?: boolean;

  /**
   * Size variant of the input
   */
  size?: 'sm' | 'md' | 'lg' | 'extraLg';

  /**
   * Full width of the input
   */
  fullWidth?: boolean;

  /**
   * Icon or element to display on the left side
   */
  startIcon?: ReactNode;

  /**
   * Icon or element to display on the right side
   */
  endIcon?: ReactNode;

  /**
   * Prefix element (more flexible than startIcon, rendered before input)
   */
  prefixElement?: ReactNode;

  /**
   * Postfix element (more flexible than endIcon, rendered after input)
   */
  postfixElement?: ReactNode;

  /**
   * Additional CSS class name for the wrapper
   */
  wrapperClassName?: string;

  /**
   * Additional CSS class name for the input element
   */
  className?: string;

  /**
   * Additional CSS class name for the input container
   */
  containerClassName?: string;
}

/**
 * Universal Input component with liquid glass effect
 *
 * Features:
 * - Liquid glass visual effect with backdrop blur
 * - Label, error, and helper text support
 * - Icon slots (start/end)
 * - Multiple size variants
 * - Full accessibility support
 * - React Hook Form integration via forwardRef
 *
 * @example
 * ```tsx
 * <Input
 *   label="Email"
 *   placeholder="Enter your email"
 *   type="email"
 *   error={errors.email?.message}
 *   startIcon={<EmailIcon />}
 *   fullWidth
 * />
 * ```
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      required,
      size = 'md',
      fullWidth = false,
      startIcon,
      endIcon,
      prefixElement,
      postfixElement,
      className,
      wrapperClassName,
      containerClassName,
      id,
      disabled,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = error ? `${inputId}-error` : undefined;
    const helperId = helperText && !error ? `${inputId}-helper` : undefined;
    const describedBy = [errorId, helperId].filter(Boolean).join(' ') || undefined;

    const wrapperClasses = classNames(
      styles.wrapper,
      {
        [styles['wrapper--full-width']]: fullWidth,
      },
      wrapperClassName
    );

    const containerClasses = classNames(
      styles.container,
      sizeStyles[size],
      {
        [styles['container--error']]: !!error,
        [styles['container--disabled']]: disabled,
        [styles['container--full-width']]: fullWidth,
      },
      containerClassName
    );

    const inputClasses = classNames(
      styles.input,
      {
        [styles['input--with-start-icon']]: !!startIcon,
        [styles['input--with-end-icon']]: !!endIcon,
      },
      className
    );

    return (
      <div className={wrapperClasses}>
        {label && (
          <label htmlFor={inputId} className={styles.label}>
            {label}
            {required && <span className={styles.required} aria-label={t('common.requiredLabel')}>*</span>}
          </label>
        )}

        <div className={containerClasses}>
          <div className={styles.filterContainer} aria-hidden="true">
            <svg width="0" height="0">
              <filter
                id={`liquid-glass-filter-${inputId}`}
                primitiveUnits="objectBoundingBox"
              >
                <feTurbulence
                  type="fractalNoise"
                  baseFrequency="0.65 2"
                  numOctaves="2"
                  seed="2"
                  result="map"
                />
                <feGaussianBlur
                  in="SourceGraphic"
                  stdDeviation="0.04"
                  result="blur"
                />
                <feDisplacementMap
                  in="blur"
                  in2="map"
                  scale="0.5"
                  xChannelSelector="R"
                  yChannelSelector="G"
                />
              </filter>
            </svg>
          </div>

          {prefixElement || (startIcon && (
            <span className={styles.startIcon} aria-hidden="true">
              {startIcon}
            </span>
          ))}

          <input
            ref={ref}
            id={inputId}
            className={inputClasses}
            disabled={disabled}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={describedBy}
            aria-required={required}
            {...props}
          />

          {postfixElement || (endIcon && (
            <span className={styles.endIcon} aria-hidden="true">
              {endIcon}
            </span>
          ))}
        </div>

        {error && (
          <p id={errorId} className={styles.errorText} role="alert">
            {error}
          </p>
        )}

        {helperText && !error && (
          <p id={helperId} className={styles.helperText}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

